import Phaser from 'phaser';
import { RunManager } from '../systems/RunManager';
import { ITEM_DEFINITIONS, SLOT_LABELS } from '../data/items';
import { PERSONAL_SLOTS } from '../types/Item.types';
import { MusicManager } from '../systems/MusicManager';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { ScrollablePanel } from '../ui/ScrollablePanel';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';

// === SCÈNE DE VICTOIRE DE RUN ===
// Affichée après avoir battu le boss final.
// Utilise InventoryManager comme source de vérité unique.

export class RunVictoryScene extends Phaser.Scene {
  private run!: RunManager;
  private panel: ScrollablePanel | null = null;

  constructor() {
    super({ key: 'RunVictoryScene' });
  }

  shutdown(): void {
    this.panel?.destroy();
    this.panel = null;
  }

  init(data: { run?: RunManager }): void {
    this.run = data?.run ?? new RunManager();
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e);
    MusicManager.playJingle(this, 'victory');

    // Titre
    this.add.text(GAME_CX, 60, '🏴‍☠️ EXPÉDITION TERMINÉE ! 🏴‍☠️', {
      fontSize: '26px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5);

    this.add.text(GAME_CX, 100, 'Le Capitaine Maudit est vaincu.\nL\'archipel maudit est libéré... pour l\'instant.', {
      fontSize: '13px', color: '#2ecc71', fontFamily: FONT_BODY, align: 'center',
      lineSpacing: 4,
    }).setOrigin(0.5);

    // Résumé du run — équipement de TOUS les membres
    const inv = this.run.getInventory();
    const transport = inv.getTransport();
    const _mob = GAME_W < 500;

    // Position du bouton final (laisse de la place sous lui)
    const btnY = _mob ? GAME_H - 80 : 500;

    // Sur mobile, on encapsule la liste de butin + stats dans un ScrollablePanel
    // pour gérer les longs butins sans rogner sur le bouton.
    const scrollTop = 170;
    const scrollBottom = btnY - 30;
    const usePanel = _mob;
    if (usePanel) {
      this.panel = new ScrollablePanel(this, 0, scrollTop, GAME_W, scrollBottom - scrollTop);
    }
    const addText = (x: number, y: number, text: string, style: Phaser.Types.GameObjects.Text.TextStyle): Phaser.GameObjects.Text => {
      const t = this.add.text(x, usePanel ? y - scrollTop : y, text, style).setOrigin(0.5);
      if (usePanel) this.panel!.add(t);
      return t;
    };

    let y = 170;
    addText(GAME_CX, y, '--- Butin récupéré ---', {
      fontSize: '14px', color: '#f39c12', fontFamily: FONT_BODY,
    });
    y += 30;

    // Équipement de chaque membre
    for (const member of this.run.getTeam()) {
      if (!member.alive) continue;
      const equip = inv.getEquipment(member.id);
      const hasEquip = PERSONAL_SLOTS.some(s => equip[s] !== null);
      if (!hasEquip) continue;
      addText(GAME_CX, y, `${member.name}:`, {
        fontSize: '11px', color: '#f1c40f', fontFamily: FONT_BODY,
      });
      y += 16;
      for (const slot of PERSONAL_SLOTS) {
        const item = equip[slot];
        if (item) {
          const def = ITEM_DEFINITIONS[item.definitionId];
          const label = SLOT_LABELS[slot] ?? slot;
          addText(GAME_CX, y, `${label}: ${def.name}`, {
            fontSize: '11px', color: '#3498db', fontFamily: FONT_BODY,
          });
          y += 16;
        }
      }
      y += 5;
    }

    // Sac à dos partagé
    const bag = inv.getBackpack();
    if (bag) {
      const bagDef = ITEM_DEFINITIONS[bag.definitionId];
      if (bagDef) {
        addText(GAME_CX, y, `🎒 ${bagDef.name}`, {
          fontSize: '11px', color: '#3498db', fontFamily: FONT_BODY,
        });
        y += 16;
      }
    }

    // Items dans le transport
    if (transport.length > 0) {
      y += 10;
      addText(GAME_CX, y, `+ ${transport.length} item(s) dans le sac`, {
        fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
      });
      y += 20;
    }

    // Stats du run
    y += 20;
    const completed = this.run.getMap().nodes.filter(n => n.status === 'completed').length;
    addText(GAME_CX, y, `Nœuds visités: ${completed} | ${this.run.getCurrentFloorLabel()}`, {
      fontSize: '13px', color: '#2ecc71', fontFamily: FONT_BODY,
    });
    y += 22;

    const finalHp = this.run.getPlayerHp();
    if (finalHp > 0) {
      addText(GAME_CX, y, `HP restants: ${finalHp}`, {
        fontSize: '13px', color: '#3498db', fontFamily: FONT_BODY,
      });
    }
    y += 22;

    if (usePanel && this.panel) {
      this.panel.setContentHeight(y - scrollTop);
    }

    // Bouton nouveau run
    const newRunBtn = this.add.text(GAME_CX, btnY, '[ 🏴‍☠️ Retour au Camp ]', {
      fontSize: '20px', color: '#f1c40f', fontFamily: FONT_BODY,
      backgroundColor: '#2c3e50', padding: { x: 20, y: 10 },
    }).setOrigin(0.5);
    newRunBtn.setInteractive({ useHandCursor: true });
    newRunBtn.on('pointerdown', () => {
      this.run.getMeta().endRun(true);
      RunManager.clearSavedRun();
      startSceneWithFade(this, 'RunEndScene', { run: this.run, victory: true }, 'radial');
    });
    newRunBtn.on('pointerover', () => newRunBtn.setColor('#ffffff'));
    newRunBtn.on('pointerout', () => newRunBtn.setColor('#f1c40f'));
  }
}
